import {all, call, put, takeEvery} from '@redux-saga/core/effects';
import {initiateTokenRefresh, toggleRefreshSpinner, TokenRefreshSessionKeys} from 'Core/ducks/refreshToken';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Conflict, Loaded, Loading} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {ValidSecurityQuestion} from 'Core/models/SecurityQuestion';
import {setRefreshReRouteOptions} from 'Core/refreshTokenUtilityMethods';
import {isRight, isLeft, left, right} from 'fp-ts/lib/Either';
import {takeLatest} from 'redux-saga/effects';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {
  changeEmail,
  changeMobilePhone,
  changeNameInformation,
  changeSecurityQuestionForm,
  changeSecurityQuestions,
  changeUserProfile,
  getUserProfile,
  saveEmail,
  saveMobilePhone,
  saveNameInformation,
  saveSecurityQuestions,
  updateDefaultAgencyAssociation,
  updateSelectedAgency,
  UserAccountActionType,
} from 'Users/ducks/userAccount';
import {SavingNameInformation, UnchangedNameInformation} from 'Users/models/NameInformation';
import {UserProfilePayload} from 'Users/models/UserProfilePayload';
import {loadClaimsAndProfile} from '../../Core/components/Startup';

export function* loadUserProfile(action: ReturnType<typeof getUserProfile>) {
  const userProfileEither: AsyncResult<UserProfilePayload> = yield call(userApi.getUserProfile, action.payload.userId);

  if (isRight(userProfileEither)) {
    const userProfile = userProfileEither.right;
    yield put(changeUserProfile(new Loaded(userProfile)));
    yield put(changeEmail(new Loaded(left(ValidEmail.create(userProfile.email)))));
    yield put(changeMobilePhone(new Loaded(left(ValidPhoneNumber.create(userProfile.mobilePhone)))));
    if (userProfile.securityQuestions && userProfile.securityQuestions.length > 0) {
      yield put(
        changeSecurityQuestionForm(
          new Loaded(
            left({
              securityQuestionOne: ValidSecurityQuestion.create(
                userProfile.securityQuestions[0].id,
                userProfile.securityQuestions[0].response
              ),
              securityQuestionTwo:
                //Added fail safe logic if we just have one security question set
                userProfile.securityQuestions.length == 1
                  ? ValidSecurityQuestion.create(
                      userProfile.securityQuestions[0].id,
                      userProfile.securityQuestions[0].response
                    )
                  : ValidSecurityQuestion.create(
                      userProfile.securityQuestions[1].id,
                      userProfile.securityQuestions[1].response
                    ),
            })
          )
        )
      );
    }
  }
}

export function* getUserProfileSaga() {
  yield takeEvery(UserAccountActionType.GET_USER_PROFILE, loadUserProfile);
}

export function* saveNameApi(action: ReturnType<typeof saveNameInformation>) {
  const {nameInformation, nyStatus, userId, dispatch, encryptedIdentifier, maskedIdentifier} = action.payload;
  yield put(
    toggleRefreshSpinner(true, `${nameInformation.firstName.value} ${nameInformation.lastName.value} Updating...`)
  );
  yield put(changeNameInformation(new SavingNameInformation(nameInformation.firstName, nameInformation.lastName)));
  yield call(
    userApi.saveName,
    userId,
    nameInformation.firstName.value,
    nameInformation.lastName.value,
    encryptedIdentifier
  );
  localStorage.setItem(TokenRefreshSessionKeys.NEWYORK_STATUS, `${nyStatus}`);
  sessionStorage.setItem('savedEncryptedId', `${encryptedIdentifier}`);
  setRefreshReRouteOptions(false);
  yield put(initiateTokenRefresh(maskedIdentifier));
  yield loadClaimsAndProfile(dispatch);
  yield put(changeNameInformation(new UnchangedNameInformation(nameInformation.firstName, nameInformation.lastName)));
}

export function* saveNameChangesSaga() {
  yield takeLatest(UserAccountActionType.SAVE_NAME_INFORMATION, saveNameApi);
}

export function* saveEmailApi(action: ReturnType<typeof saveEmail>) {
  const {email, originalEmail, encryptedIdentifier, userId, dispatch, maskedIdentifier} = action.payload;
  yield put(changeEmail(new Loading()));
  const response = yield call(userApi.saveEmail, userId, email.address, encryptedIdentifier);
  if (isRight(response)) {
    yield put(createSnackbar('Email successfully updated!'));
    yield put(changeEmail(new Loaded(left(email))));
    yield put(initiateTokenRefresh(maskedIdentifier));
  } else if (isLeft(response)) {
    const errorMsg =
      response.left instanceof Conflict ? response.left.errorDetails : 'Something went wrong. Please try again later.';
    yield put(createSnackbar(errorMsg));
    yield put(changeEmail(new Loaded(right(originalEmail))));
  }
  yield loadClaimsAndProfile(dispatch);
}

export function* saveEmailChangesSaga() {
  yield takeEvery(UserAccountActionType.SAVE_EMAIL, saveEmailApi);
}

export function* saveMobilePhoneApi(action: ReturnType<typeof saveMobilePhone>) {
  const {phone, encryptedIdentifier, userId, dispatch} = action.payload;
  yield put(changeMobilePhone(new Loading()));
  yield call(userApi.saveMobilePhone, userId, phone.phoneNumber, encryptedIdentifier);
  yield loadClaimsAndProfile(dispatch);
  yield put(changeMobilePhone(new Loaded(left(phone))));
}

export function* saveMobilePhoneChangesSaga() {
  yield takeEvery(UserAccountActionType.SAVE_PHONE, saveMobilePhoneApi);
}

export function* getSecurityQuestionsSaga() {
  yield takeEvery(
    UserAccountActionType.GET_SECURITY_QUESTIONS,
    getApiCallGenerator(userApi.getSecurityQuestions, changeSecurityQuestions)
  );
}

export function* saveSecurityQuestionsApi(action: ReturnType<typeof saveSecurityQuestions>) {
  const {securityQuestionForm, encryptedIdentifier, userId, dispatch} = action.payload;
  yield put(changeSecurityQuestionForm(new Loading()));
  yield call(
    userApi.saveSecurityQuestions,
    userId,
    securityQuestionForm.securityQuestionOne,
    securityQuestionForm.securityQuestionTwo,
    encryptedIdentifier
  );
  yield loadClaimsAndProfile(dispatch);
  yield put(changeSecurityQuestionForm(new Loaded(left(securityQuestionForm))));
}

export function* saveSecurityQuestionSaga() {
  yield takeEvery(UserAccountActionType.SAVE_SECURITY_QUESTIONS, saveSecurityQuestionsApi);
}

function* updateDefaultAgencyAssociationApi(action: ReturnType<typeof updateDefaultAgencyAssociation>) {
  const {id, encryptedIdentifier, isNewYork} = action.payload;
  yield call(userApi.updateDefaultAgencyAssociation, id, encryptedIdentifier, isNewYork);
  if (isNewYork !== undefined) {
    localStorage.setItem(TokenRefreshSessionKeys.NEWYORK_STATUS, `${isNewYork}`);
  }
}

function* updateSelectedAgencyAssociation(action: ReturnType<typeof updateSelectedAgency>) {
  const {encryptedIdentifier, isNewYork} = action.payload;
  yield call(userApi.updateSelectedAgencyAssociation, encryptedIdentifier);
  if (isNewYork !== undefined) {
    localStorage.setItem(TokenRefreshSessionKeys.NEWYORK_STATUS, `${isNewYork}`);
  }
}

export function* updateDefaultAgencyAssociationSaga() {
  yield takeLatest(UserAccountActionType.UPDATE_DEFAULT_AGENCY_ASSOCIATION, updateDefaultAgencyAssociationApi);
}

export function* watchUpdateSelectedAgencyAssociation() {
  yield takeLatest(UserAccountActionType.UPDATE_SELECTED_AGENCY, updateSelectedAgencyAssociation);
}

export default function* userSaga() {
  yield all([
    saveSecurityQuestionSaga(),
    saveNameChangesSaga(),
    getUserProfileSaga(),
    saveEmailChangesSaga(),
    saveMobilePhoneChangesSaga(),
    getSecurityQuestionsSaga(),
    updateDefaultAgencyAssociationSaga(),
    watchUpdateSelectedAgencyAssociation(),
  ]);
}
